import React, {useMemo, useState} from 'react';
import {Button, Card, Tooltip, Segmented} from 'antd';
import {Link} from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {internalApi} from '../../common/axios';
import {useQueryBase} from '../../shared/hooks/useHookBase';
import './home.scss';

dayjs.locale('ko');

const DAY_OPTIONS = [1, 3, 7, 14, 30];
const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 48;
const MAX_VISIBLE_ROWS = 10;

const fetchEventTimetable = async () => {
    const response = await internalApi.get('/events/timetable');
    return response.data.rows;
};

const fetchShoppingRoadTimetable = async () => {
    const response = await internalApi.get('/events/shopping_road/timetable');
    return response.data.rows;
};

const fetchProductTimetable = async () => {
    const response = await internalApi.get('/store_manager/timetable');
    return response.data.rows;
};

const useEventTimetable = () => useQueryBase('timetableEvents', fetchEventTimetable);
const useShoppingRoadTimetable = () => useQueryBase('timetableShoppingRoad', fetchShoppingRoadTimetable);
const useProductTimetable = () => useQueryBase('timetableProducts', fetchProductTimetable);

const getEventColor = (item) => item.onOff === 0 ? '#bfbfbf' : '#1890ff';

const getShoppingRoadColor = (item) => item.onOff === 0 ? '#bfbfbf' : '#fa541c';
const getProductColor = (item) => item.onOff === 0 ? '#bfbfbf' : '#7b61ff';

const Legend = ({items}) => (
    <div className="timetable-legend">
        {items.map((item, i) => (
            <span key={i} className="timetable-legend-item">
                {item.colors
                    ? item.colors.map((c, j) => (
                        <span key={j} className="timetable-legend-dot" style={{background: c}}/>
                    ))
                    : <span className="timetable-legend-dot" style={{background: item.color}}/>
                }
                <span className="timetable-legend-label">{item.label}</span>
            </span>
        ))}
    </div>
);

const Timetable = ({items, getLabel, getColor, loading, days, legend}) => {
    const windowStart = dayjs().subtract(1, 'hour');
    const windowStartUnix = windowStart.unix();
    const windowEndUnix = windowStart.add(days, 'day').unix();
    const windowTotalSec = days * 86400;
    const nowPct = 3600 / windowTotalSec * 100;

    const dayList = Array.from({length: days}, (_, i) => dayjs().startOf('day').add(i, 'day'));

    const visibleItems = useMemo(() =>
        (items || [])
            .filter(item => item.begin < windowEndUnix && item.end > windowStartUnix)
            .sort((a, b) => a.begin - b.begin),
        [items, windowStartUnix, windowEndUnix]
    );

    const maxHeight = HEADER_HEIGHT + MAX_VISIBLE_ROWS * ROW_HEIGHT;

    return (
        <div>
        {legend && <Legend items={legend}/>}
        <div className="timetable-scroll" style={{maxHeight}}>
            <div className="timetable-inner">
                {/* Header */}
                <div className="timetable-header-row">
                    <div className="timetable-label timetable-label--header">항목</div>
                    <div className="timetable-timeline">
                        {dayList.map((day, i) => (
                            <div
                                key={i}
                                className={`timetable-day-header${day.isSame(dayjs(), 'day') ? ' timetable-day-header--today' : ''}`}
                            >
                                <span>{day.format('MM/DD')}</span>
                                <span className="timetable-dow">{day.format('ddd')}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Rows */}
                {loading && <div className="timetable-empty">로딩 중...</div>}
                {!loading && visibleItems.length === 0 && (
                    <div className="timetable-empty">해당 기간에 항목이 없습니다.</div>
                )}
                {visibleItems.map(item => {
                    const beginPct = Math.max(0, (item.begin - windowStartUnix) / windowTotalSec * 100);
                    const endPct = Math.min(100, (item.end - windowStartUnix) / windowTotalSec * 100);
                    const barLeft = `${beginPct}%`;
                    const barWidth = `${Math.max(endPct - beginPct, 0.5)}%`;

                    return (
                        <div key={item.seq} className="timetable-row">
                            <div className="timetable-label" title={getLabel(item)}>
                                {getLabel(item)}
                            </div>
                            <div className="timetable-timeline">
                                {dayList.map((day, i) => (
                                    <div
                                        key={i}
                                        className={`timetable-cell${day.isSame(dayjs(), 'day') ? ' timetable-cell--today' : ''}`}
                                    />
                                ))}
                                <div className="timetable-now-line" style={{left: `${nowPct}%`}}/>
                                <Tooltip
                                    title={`${dayjs.unix(item.begin).format('YYYY-MM-DD HH:mm')} ~ ${dayjs.unix(item.end).format('YYYY-MM-DD HH:mm')}`}
                                >
                                    <div
                                        className="timetable-bar"
                                        style={{left: barLeft, width: barWidth, background: getColor(item)}}
                                    >
                                        <span className="timetable-bar-text">{getLabel(item)}</span>
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
        </div>
    );
};

const EVENT_LEGEND = [
    {color: '#1890ff', label: 'On'},
    {color: '#bfbfbf', label: 'Off'},
];
const SHOPPING_ROAD_LEGEND = [
    {color: '#fa541c', label: 'On'},
    {color: '#bfbfbf', label: 'Off'},
];
const PRODUCT_LEGEND = [
    {color: '#7b61ff', label: 'On'},
    {color: '#bfbfbf', label: 'Off'},
];

const Home = () => {
    const [days, setDays] = useState(1);
    const {data: events, isLoading: eventsLoading} = useEventTimetable();
    const {data: shoppingRoads, isLoading: srLoading} = useShoppingRoadTimetable();
    const {data: products, isLoading: productsLoading} = useProductTimetable();

    return (
        <div className="home-dashboard">
            <div className="home-day-control">
                <span className="home-day-control__label">기간</span>
                <Segmented
                    value={days}
                    onChange={setDays}
                    options={DAY_OPTIONS.map(d => ({label: `${d}일`, value: d}))}
                />
            </div>
            <Card title="이벤트 스케줄" extra={<Link to="/event"><Button size="small">바로가기</Button></Link>}>
                <Timetable
                    days={days}
                    legend={EVENT_LEGEND}
                    items={events || []}
                    getLabel={item => {
                        const name = item.typeName || `typeIdx:${item.typeIndex}`;
                        return item.memo ? `[${name}] ${item.memo}` : `[${name}] #${item.seq}`;
                    }}
                    getColor={getEventColor}
                    loading={eventsLoading}
                />
            </Card>
            <Card title="쇼핑로드 스케줄" style={{marginTop: 16}} extra={<Link to="/event/shoppingRoad"><Button size="small">바로가기</Button></Link>}>
                <Timetable
                    days={days}
                    legend={SHOPPING_ROAD_LEGEND}
                    items={shoppingRoads || []}
                    getLabel={item => item.memo ? `[${item.seq}] ${item.memo}` : `쇼핑로드 ${item.seq}`}
                    getColor={getShoppingRoadColor}
                    loading={srLoading}
                />
            </Card>
            <Card title="상품 스케줄" style={{marginTop: 16}} extra={<Link to="/shopPackage"><Button size="small">바로가기</Button></Link>}>
                <Timetable
                    days={days}
                    legend={PRODUCT_LEGEND}
                    items={products || []}
                    getLabel={item => item.packageName ? `[${item.seq}] ${item.packageName}` : `상품 ${item.seq}`}
                    getColor={getProductColor}
                    loading={productsLoading}
                />
            </Card>
        </div>
    );
};

export default Home;
